import type { AiCapabilityPage } from "../schema.js";
import { LIVE, OWNER_ADMIN } from "./_shared.js";

/**
 * `GRP-BANK-QR` (@UC0216) — trang **redirect** (US-AIV2-0049): chat KHÔNG hỏi ô, KHÔNG có nút Đồng ý,
 * chỉ một câu giải thích + nút mở đúng màn hình `/groups/:groupId/settings` (ADR-0009 bất biến 5).
 * Cửa mobile trang này chịu trách nhiệm (ĐỌC tài khoản là trang chat `GROUP-BANK-ACCOUNT`; ĐẶT/ĐỔI ở đây — đổi số nhận tiền bằng câu chữ là đường ngắn nhất tới lừa đảo).

 * 🔴 Vai HẸP HƠN cảm giác ban đầu (US-AIV2-0049, review #827 H1 — ĐO từ guard thật, đừng nới lại):
 *   cả ba cửa (bank-account, qr-image PUT/DELETE) gác `@RequireGroupRoles(OWNER, ADMIN)` — thủ quỹ đổi số tài khoản là 403.
 *   Trang redirect chỉ mở màn hình, nên mời sai vai = dẫn người ta tới nút bấm vào là 403
 *   (CLAUDE.md §4 cấm: "dẫn member thường vào form quản trị rồi để 403 ở bước chốt").
 *   Khoá bằng `roles-parity.invariants.spec.ts` — nới ở đây mà GUI không nới thì test ĐỎ.
 */
export const GRP_BANK_QR = {
  id: "GRP-BANK-QR",
  uc: "@UC0216",
  tier: "redirect",
  status: LIVE,
  roles: OWNER_ADMIN,
  flags: [],
  slots: [],
  llm: {
    description:
      "ĐẶT, ĐỔI hoặc XOÁ số tài khoản ngân hàng nhận tiền của NHÓM và ảnh mã QR — việc của quản lý. 'Xoá tài khoản nhận tiền' ở đây là xoá SỐ TÀI KHOẢN NGÂN HÀNG của nhóm, KHÔNG phải xoá tài khoản đăng nhập của người dùng (đó là ACCT-DELETE). Đổi SỐ TÀI KHOẢN thì ở đây; đổi NGƯỜI LÀM CHỦ NHÓM thì không (đó là GRP-OWNERSHIP). Chỉ XEM số tài khoản hay mã QR để chuyển tiền cũng không phải trang này.",
    examples: [
      "đổi số tài khoản nhận tiền",
      "doi so tai khoan nhan tien",
      "cập nhật stk của nhóm",
      "đổi mã qr",
      "xoá qr",
      "up qr mới",
      // ——— 0105: thêm cho đủ 40 ———
      "thay số tài khoản nhận quỹ",
      "thay so tai khoan nhan quy",
      "đặt tài khoản ngân hàng cho nhóm",
      "dat tai khoan ngan hang cho nhom",
      "sửa lại tài khoản nhận tiền của nhóm",
      "sua lai tai khoan nhan tien cua nhom",
      "tôi đổi ngân hàng rồi, cập nhật lại cho nhóm",
      "toi doi ngan hang roi cap nhat lai cho nhom",
      "tải ảnh qr mới lên",
      "tai anh qr moi len",
      "gỡ ảnh mã qr cũ đi",
      "go anh ma qr cu di",
      "thêm mã qr chuyển khoản cho quỹ",
      "them ma qr chuyen khoan cho quy",
      "cài số tài khoản để mọi người chuyển vào",
      "cai so tai khoan de moi nguoi chuyen vao",
      "đổi tài khoản nhận tiền kiểu gì?",
      "doi tai khoan nhan tien kieu gi",
      "cập nhật thông tin ngân hàng của quỹ",
      "cap nhat thong tin ngan hang cua quy",
      "thay qr code của nhóm",
      "thay qr code cua nhom",
      "nhập lại số tài khoản cho quỹ lớp",
      "nhap lai so tai khoan cho quy lop",
      "xoá tài khoản nhận tiền hiện tại",
      "xoa tai khoan nhan tien hien tai",
      "đổi tên người thụ hưởng trên stk của quỹ",
      "doi ten nguoi thu huong tren stk cua quy",
      "sửa stk nhóm bị sai",
      "sua stk nhom bi sai",
      "muốn thay đổi mã qr nhận tiền",
      "muon thay doi ma qr nhan tien",
      "đặt lại ảnh qr cho đội bóng",
      "dat lai anh qr cho doi bong",
    ],
  },
  screen: "/groups/:groupId/settings",
  screenShows: "bank_account_setup",
  endpoints: [
    "PUT /groups/{groupId}/bank-account",
    "PUT /groups/{groupId}/qr-image",
    "DELETE /groups/{groupId}/qr-image",
  ],
  facts: [],
  templates: {
    /*
     * 🔴 GUIDE-SCREEN-TRUTH (G-1, @UC0216): trang này còn được mở từ nút «Cài tài khoản nhận tiền» của thẻ `DUTY-NO-BANK-ACCOUNT` (nhóm CHƯA có
     * tài khoản) — câu chỉ nói "đổi… số cũ" là sai tình huống. Trang redirect KHÔNG có cơ chế biến thể câu theo dữ liệu (`buttonLabelFrom` của
     * tourguide-0013 chỉ đổi nhãn nút ĐI TIẾP của một kết quả trang đọc/ghi) ⇒ MỘT câu + nhãn đúng cả hai tình huống, không dựng cơ chế mới.
     */
    redirect: "Mình mở cài đặt nhóm để bạn cài hoặc đổi tài khoản nhận tiền nhé. Nếu là đổi số, nhớ báo cả nhóm — người đang chuyển dở dễ chuyển vào số cũ.",
  },
  // `help.title` ("Đổi…") vào chỉ mục tìm trang ⇒ giữ (0 ca eval đổi); nhãn NÚT mở màn thì nói đúng cả hai tình huống.
  screenLabel: "Cài hoặc đổi tài khoản nhận tiền",
  journey: { after: ["GROUP-BANK-ACCOUNT"] },
  help: { title: "Đổi tài khoản nhận tiền", tryNow: "đổi số tài khoản nhận tiền" },
} satisfies AiCapabilityPage;
